import { h, defineComponent, ref, watch } from 'vue';
import { d, n, t } from '@/ui/i18n/i18n';
import { box, column, indexView, level, loader, pageSubtitle, pageTitle, sortableNumericColumn } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, intoEditable } from '@/services/api/resources/sensor';
import { all as getAllMeasurements } from '@/services/api/resources/measurement';
import { Button } from '@/ui/components/Button';
import { IdProps, sequence } from '@/ui/helpers/uiHelper';
import * as qs from '@/ui/queryState';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { MeasurementList, MeasurementRecord } from '@/entities/measurement';
import { dateRangeFilter } from '@/ui/components/filters';
// @ts-ignore
import { BTable } from 'buefy/dist/esm/table';
import { NothingToDisplayMessage } from '@/ui/components/message/NothingToDisplayMessage';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { SensorMeasurementsChart } from '@/ui/pages/sensor/SensorMeasurementsChart';
import { useSensorRecord } from '@/ui/pages/sensor/useSensorRecord';
import { endsToday, startsLast30Days } from '@/ui/helpers/dateHelper';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { emptyMeta } from '@/services/api';

export const SensorMeasurementsPage = defineComponent<IdProps>({
  name: 'SensorMeasurementsPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('sensors')) {
      return renderResourceTeaserPage(t('common.sensors'));
    }

    if (!hasPermission(Permissions.MANAGE_HARDWARE)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // store state in query string
    const start = qs.date('start', startsLast30Days());
    const end = qs.date('end', endsToday());

    // get sensor and measurements
    const measurements = ref<MeasurementList>({
      data: [],
      meta: emptyMeta(),
    });
    const sensor = useSensorRecord();
    const getRecord = useTask(
      () => sequence(get(props.id), getAllMeasurements(props.id, start.value, end.value)),
      ([{ data }, measurementList]) => {
        sensor.value = intoEditable(data);
        measurements.value = measurementList;
        initialLoading.value = false;
      },
    );
    watch([ref(props.id), start, end], getRecord.run);

    return function render() {
      return h('div', { class: 'sensor-measurement-page' }, [
        indexView(
          [
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            dateRangeFilter(start, end, () => {}, 3),
          ], [
            level(
              [pageTitle(t('sensors.measurements'))],
              [h(Button, {
                attrs: {
                  tag: 'router-link',
                  to: {
                    name: 'sensorEdit',
                    id: props.id.toString(),
                  },
                  iconLeft: 'edit',
                },
              }, t('sensors.edit'))],
            ),
            loader('div', getRecord, [
              box(
                [
                  measurements.value.data.length > 0
                    ? h(
                      SensorMeasurementsChart, {
                        props: {
                          measurements: measurements.value,
                          start: start.value,
                          end: end.value,
                          sensor: sensor.value,
                        },
                      },
                    ) : h(NothingToDisplayMessage),
                ],
              ),
              box([
                pageSubtitle(sensor.value ? sensor.value.title : ''),
                h(
                  BTable,
                  {
                    props: {
                      data: measurements.value.data,
                      paginated: measurements.value.data.length > 0,
                      perPage: 20,
                      defaultSort: ['@', 'desc'],
                      sortIcon: 'angle-up',
                      iconPack: 'far',
                    },
                    scopedSlots: {
                      empty: () => h(NothingToDisplayMessage),
                    },
                  }, [
                    column({
                      field: '@',
                      label: t('sensors.measured-at'),
                      sortable: true,
                    }, {
                      default: ({ row: measurement }: { row: MeasurementRecord }) =>
                        d(measurement['@'], 'shortDateTime'),
                    }),
                    sortableNumericColumn({
                      field: 't',
                      label: t('sensors.temperature'),
                    }, {
                      default: ({ row: measurement }: { row: MeasurementRecord }) =>
                        measurement.t === null ? '' : n(measurement.t, 'metricTemperature'),
                    }),
                    sortableNumericColumn({
                      field: 'h',
                      label: t('sensors.humidity'),
                    }, {
                      default: ({ row: measurement }: { row: MeasurementRecord }) =>
                        measurement.h === null ? '' : n(measurement.h, 'metricHumidity'),
                    }),
                  ],
                ),
              ]),
            ], !initialLoading.value),
          ],
        ),
      ]);
    };
  },
});
